import React, { useEffect, useState } from "react";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react"; // React Grid Logic
import "ag-grid-community/styles/ag-grid.css"; // Core CSS
import "ag-grid-community/styles/ag-theme-quartz.css"; // Theme
import { GridReadyEvent } from "ag-grid-community";
import AgGridService from "../../../shared/services/ag-grid/ag-grid.service";
import IException from "../../../shared/contracts/IException";
import useLoader from "../../../shared/components/loading-dialog/useLoader.hook";
import LoadingDialogComponent from "../../../shared/components/loading-dialog/loading-dialog.component";
import IQueuedReport from "../../../shared/contracts/IQueuedReport";
import useReportApi from "./useReportApi";
import QueuedReportsService from "./queued-reports.service";
import QueuedReportsToolbarComponent from "./components/queued-reports-toolbar/queued-reports-toolbar.component";
import CloudDownloadRenderer from "./cell-renderers/cloud-download.cell-renderer";
import MenuCellRenderer from "./cell-renderers/menu-cell-renderer";
import ToastProvider from "../../../shared/context/toast/toast-context";
import useAppStoreContext from "../../../shared/context/app-store/app-store.context";

function QueuedReportsPage() {
  const { getQueuedReportsList } = useReportApi();
  const { queuedReportsList, setQueuedReportsList } = useAppStoreContext();
  const [
    showCreateReportSubscriptionDialog,
    setShowCreateReportSubscriptionDialog,
  ] = useState(false);
  const [isReloadData, setIsReloadData] = useState(false);
  const { getDefaultColDef, getDefaultProps } = new AgGridService();

  const queuedReportsListLoader = useLoader({
    loaderName: "Queued Reports",
    loadAsync: () =>
      getQueuedReportsList({
        doIf: () => queuedReportsListLoader.isNotLoading,
        doBefore: () => {
          queuedReportsListLoader.SET_LOADING("Fetching...");
          setQueuedReportsList([]);
        },
        onFulfilled: (data) => {
          setQueuedReportsList(data);
          queuedReportsListLoader.SET_SUCCESS("Fetch Success!");
        },
        onException: (exception: IException) => {
          queuedReportsListLoader.SET_FAILURE(
            exception.title + " - " + exception.message
          );
        },
      }),
  });

  useEffect(() => {
    queuedReportsListLoader.loadAsync();
  }, [isReloadData]);

  const handleGridReady = (event: GridReadyEvent) => {
    event.api.sizeColumnsToFit();
  };

  const onClickDownload = (report: IQueuedReport) => {
    console.log("Downloading report:", report);
  };

  const gridStyle = {
    height: "85vh", // Similar to ReportListPage
    width: "100%",
    maxWidth: "1800px",
    margin: "auto",
    padding: "1rem",
  };

  const columnDefs = QueuedReportsService.colDefs.map((colDef) => ({
    ...colDef,
    filter: true,
    resizable: true,
    autoHeight: true,
    // Apply flex only to certain columns that can expand or shrink
    flex:
      colDef.field === "reportName" || colDef.field === "createdByUserLogin"
        ? 1
        : 0,
    width: colDef.width ? colDef.width : null,
    minWidth: 100,
    cellStyle: { whiteSpace: "normal" },
  }));

  return (
    <ToastProvider>
      <LoadingDialogComponent loaderHook={queuedReportsListLoader} />
      <QueuedReportsToolbarComponent
        onReload={queuedReportsListLoader.loadAsync}
        handleCreateReportSubscriptionClick={() =>
          setShowCreateReportSubscriptionDialog(true)
        }
      />
      <div className="ag-theme-quartz" style={gridStyle}>
        <AgGridReact
          {...getDefaultProps({})}
          onGridReady={handleGridReady}
          defaultColDef={getDefaultColDef()}
          columnDefs={QueuedReportsService.colDefs}
          rowData={queuedReportsList || []}
          frameworkComponents={{
            cloudDownloadRenderer: CloudDownloadRenderer,
            menuCellRenderer: MenuCellRenderer,
          }}
          pagination={true}
          paginationPageSize={20}
          onCellClicked={(params) => {
            if (params.colDef.field === "file") {
              onClickDownload(params.data);
            }
          }}
        />
      </div>
    </ToastProvider>
  );
}

export default QueuedReportsPage;
